import { Component, OnInit, DestroyRef, inject, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { distinctUntilChanged } from "rxjs";
import { finalize } from "rxjs/operators";

// Services
import { MsalService } from "@azure/msal-angular";
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { CreateDistributorService } from "../../../data/distributor/create-distributor.service";
import { UpdateDistributorService } from "src/app/data/distributor/update-distributor.service";
import { ListDistributorAgentsService } from "src/app/data/distributor/list-distributor-agents.service";
import { AssignDistributorAgentService } from "src/app/data/distributor/assign-distributor-agent.service";
import dayjs from "dayjs";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";

import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { DISTRIBUTOR_DETAILS_I18N } from "./distributor-details.i18n";
import { PRICING_MODEL, BILLING_FREQUENCY } from "src/app/constants/app-constants";
import { Addresses, AddressesFormComponent, AddressesDefaultValue } from "../../shared/addresses-form/addresses-form.component";
import { ContactDetails, ContactDetailsFormComponent, ContactDetailsDefaultValue } from "../../shared/contact-details-form/contact-details-form.component";
import { BillingInfo, BillingInfoDefaultValue, BillingInfoFormComponent } from "../../shared/billing-info-form/billing-info-form.component";
import { DistributorAgentInviteComponent } from "../distributor-agent-invite/distributor-agent-invite.component";
import { GetParameterService } from "../../../data/record/get-parameter.service";
import { UserDataTemplateListComponent } from "../user-data-template-list/user-data-template-list.component";
import { DebugUtils } from "src/app/shared/utils/debug-utils";

@Component({
  selector: "app-distributor-details",
  templateUrl: "./distributor-details.component.html",
  styleUrls: ["./distributor-details.component.css"],
  imports: [
    SHARED_IMPORTS,
    AddressesFormComponent,
    ContactDetailsFormComponent,
    BillingInfoFormComponent,
  ]
})
export class DistributorDetailsComponent implements OnInit {
  @ViewChild('billingInfoFormComp') billingInfoFormComponent!: BillingInfoFormComponent;
  
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = DISTRIBUTOR_DETAILS_I18N;
  public pricingModel = PRICING_MODEL;
  public billingFrequency = BILLING_FREQUENCY;

  public distributorDocument: DistributorDocument;

  public mode = DistributorDetailMode.disp;
  Mode = DistributorDetailMode;

  returnToUrl;
  homeUrl;

  errors;
  previews;

  userDataTemplates;
  canUpdate;
  hasSectionGroupAdminManagement = false;
  hasAllSectionGroupManagement;
  hasDistributorManagement = false;

  public agents = signal<any[]>([]);
  public billToInfos = signal<any[]>([]);

  changesMade = false;
  isSending = signal(false);

  distributorFormGroup;
  private readonly defaultDistributorData: DistributorDocument = {
    id: null,

    name: "",

    addresses: AddressesDefaultValue,
    contactDetails: ContactDetailsDefaultValue,
    billingInfo: BillingInfoDefaultValue,
    // billingSource: BillingSourceDefaultValue,
    // billingTerms: BillingTermsDefaultValue,

    commonInfo:{
      lastModifiedAt: "",
      lastModifiedBy: "",
    },

    maxSectionGroups : 10,

    functionAttributes: [],
    userDataTemplateChoices: [],

    agents: [], // 表示専用
  };

  contractStatus = [
    { value: "OPEN", viewValue: DISTRIBUTOR_DETAILS_I18N.contractStatus.active },
    { value: "CLOSE", viewValue: DISTRIBUTOR_DETAILS_I18N.contractStatus.inactive },
  ];

  // assignAgent = [
  //   { value: false, desc: DISTRIBUTOR_DETAILS_I18N.assignAgent.later },
  //   { value: true, desc: DISTRIBUTOR_DETAILS_I18N.assignAgent.self },
  // ];

  distributorId: any;

  constructor(
    public dialogRef: MatDialogRef<DistributorDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _matdialog: MatDialog,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private msalService: MsalService,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar,
    public common: DatabaseCacheService,
    private createDistributorService: CreateDistributorService,
    private updateDistributorService: UpdateDistributorService,
    private listDistributorAgentService: ListDistributorAgentsService,
    private assignDistributorAgentService : AssignDistributorAgentService,
    private router: Router,
    public dialog: MatDialog,
    private getParameterService: GetParameterService,
    private userDataTemplateListComponent: UserDataTemplateListComponent,
  ) {}

  ngOnInit() {
    
    // 新規作成・更新が終わったあとで戻るURL
    this.returnToUrl = this.router.url;
    const secondSlashIndex = this.returnToUrl.indexOf("/", 1); // 最初のスラッシュ以降を検索
    this.homeUrl =
      secondSlashIndex !== -1
        ? this.returnToUrl.substring(0, secondSlashIndex)
        : this.returnToUrl;

    this.distributorDocument = this.data?.distributorDocument ?? this.defaultDistributorData;
    this.distributorId = this.distributorDocument.id;
    this.userDataTemplates = this.data?.userDataTemplates;
    console.log("received distributorData", this.distributorDocument);
    console.log("received userDataTemplates", this.userDataTemplates);

    console.log("distributor detail, ngOnInit, userDataTemplates=", this.userDataTemplates);

    this.distributorFormGroup = new FormGroup({
      // assignAgent: new FormControl(""), // 新規作成時に自分自身をエージェントとして割り当てるかを指定する。新規作成時だけ表示し、データベースには保存しない。
   
      id: new FormControl(""),
      type: new FormControl("distributor"),
      name: new FormControl("", [Validators.required]),
      maxSectionGroups: new FormControl(""),

      // 共通パーツはそれぞれのサブコンポーネントで定義している
      addresses: AddressesFormComponent.createAddressesGroup(),
      contactDetails: ContactDetailsFormComponent.createContactDetailsGroup(),
      billingInfo: BillingInfoFormComponent.createBillingInfoGroup(),

      functionAttributes: new FormGroup({}),
      userDataTemplateChoices: new FormGroup({}),

      agents: new FormControl(""),
      
      commonInfo: new FormGroup({
        lastModifiedAt: new FormControl(""),
        lastModifiedBy: new FormControl("")
      })
    });

    this.setChangeEvents();

    // functionAttributesの要素はときどき変化するため動的に追加するようにする
    const functionAttributesGroup = this.distributorFormGroup.get('functionAttributes') as FormGroup;
    if (functionAttributesGroup) {
      this.common.SectionGroupFunctions.forEach((f) =>
        {
          // debugger;
          functionAttributesGroup.addControl(f.code, new FormControl(false));
        }
      );
    }
    this.distributorDocument.functionAttributes?.forEach((f) => {
      this.distributorFormGroup.get(`functionAttributes.${f}`)?.setValue(true, { emitEvent: false });
    });

    // userDataTemplatesの要素はときどき変化するため動的に追加するようにする
    const userDataTemplatesGroup = this.distributorFormGroup.get('userDataTemplateChoices') as FormGroup;
    if (userDataTemplatesGroup) {
      this.userDataTemplates.forEach((u) =>
        userDataTemplatesGroup.addControl(this.getUdFormControlName(u.id), new FormControl(false))
      );
    }
    this.distributorDocument.userDataTemplateChoices?.forEach((u) => {
      var uid = this.getUdFormControlName(u);
      console.log("setting uid=",uid,"to true");
      this.distributorFormGroup.get(`userDataTemplateChoices.${uid}`)?.setValue(true, { emitEvent: false });
    });
    
    this.distributorFormGroup.disable();

    this.distributorFormGroup.patchValue(this.distributorDocument);




    // if (this.common.hasAllSectionGroupManagement()) {
    //   this.distributorFormGroup.get("assignAgent")?.setValue(false);
    // } else {
    //   // 現在のユーザーが All SectoinGroup Managementを持っていないときは強制的に現在のユーザーが
    //   this.distributorFormGroup.get("assignAgent")?.setValue(true);
    // }


    if (this.distributorDocument.id == null) {
      this.enableInput();
      this.mode = DistributorDetailMode.create;
    } else {
      this.listAgents();
    }

    this.permissionCheck();
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.hasAllSectionGroupManagement = this.common.hasAllSectionGroupManagement();
        this.permissionCheck();
      });
  }

  permissionCheck() {
    this.canUpdate = this.common.hasAllSectionGroupManagement();

    this.canUpdate ||= this.common.hasSectionGroupCreate();
    if (this.distributorId != null) {
      this.hasDistributorManagement = this.common.hasDistributorAgentRole(this.distributorId);
      this.hasSectionGroupAdminManagement =   this.common.hasSectionGroupAdminManagement(this.distributorId);

      this.canUpdate ||=  this.hasDistributorManagement;
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  switchToEditMode() {
    this.mode = DistributorDetailMode.edit;

    this.enableInput();
  }

  enableInput() {
    // this.distributorFormGroup.get("assignAgent").enable();

    this.distributorFormGroup.get("name").enable();

    this.distributorFormGroup.get("addresses").enable(); // addresses の下にぶら下がっているコントロールをすべて enable
    this.distributorFormGroup.get("contactDetails").enable(); // 以下同文

    if(this.common.hasDistributorManagement()){
      this.distributorFormGroup.get("billingInfo").enable();
  
      this.distributorFormGroup.get("maxSectionGroups").enable();
      this.distributorFormGroup.get("functionAttributes").enable();
      this.distributorFormGroup.get("userDataTemplateChoices").enable();

      // 下位のフォームグループの 値変更イベントハンドラ を呼び出すことで、連鎖的に下位のグループの項目の enable / disable を行なう
      this.billingInfoFormComponent?.onHasCustomBillingRuleChange(this.distributorFormGroup.get("billingInfo.hasCustomBillingRule")?.value);
    }


    this.distributorFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.changesMade = true;
      });

    return;
  }

  listAgents() {
    this.listDistributorAgentService
      .listDistributorAgents(this.distributorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          console.log("Received agents", res);
          this.agents.set(res as any[]);
        },
        (error) => {
        },
      );
  }

  onEditOrUploadClicked() {
    // 現在がディスプレイモードなら編集モードに切り替える
    if (this.mode == DistributorDetailMode.disp) {
      this.switchToEditMode();
      return;
    }

    //　各フィールドにValidationRuleを適用し確認
    this.distributorFormGroup.markAllAsTouched();

    if (!this.distributorFormGroup.valid) {
      DebugUtils.dumpFormStructure(this.distributorFormGroup);

      let snack = this.snackBar.open(
        this.i18n.validation.checkFields,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );
      return;
    }

    var postBody: Record<string, any> = {};

    // postBody.assignAgent = this.distributorFormGroup.value.assignAgent;

    postBody = this.distributorFormGroup.getRawValue();

    // 1. 
    postBody.functionAttributes = [];
    let noFunctionsSelected = true;
    // 2. フォームの functionAttributes グループから、各関数の true/false 状態を一括取得
    const functionValues = this.distributorFormGroup.get('functionAttributes')?.value || {};
    // 3. ループを回して、値が true になっている関数のコードだけを配列にプッシュ
    this.common.SectionGroupFunctions.forEach((f) => {
      if (functionValues[f.code]) {
        postBody.functionAttributes.push(f.code);
        noFunctionsSelected = false;
      }
    });

    // 1.   
    postBody.userDataTemplateChoices = [];
    let noUserDataTemplatesSelected = true;
    // 2. フォームの userDataTemplateChoices グループから、各関数の true/false 状態を一括取得
    const userDataTemplateChoicesValues = this.distributorFormGroup.get('userDataTemplateChoices')?.value || {};
    // 3. ループを回して、値が true になっている関数のコードだけを配列にプッシュ
    this.userDataTemplates.forEach((u) => {
      console.log("userTemplates, id =", u.id, ", formcontrol =", this.getUdFormControlName(u.id), "formValue =", userDataTemplateChoicesValues[this.getUdFormControlName(u.id)] );
      if (userDataTemplateChoicesValues[this.getUdFormControlName(u.id)]) {
        postBody.userDataTemplateChoices.push(u.id);
        noUserDataTemplatesSelected = false;
      }
    });

    
    this.isSending.set(true);
    if (this.distributorId == null) {
      this.createDistributor(postBody);
    } else {
      this.updateDistributor(postBody);
    }
  }

  getUdFormControlName(id:any){
    return `ud${id}`;
  }

  createDistributor(postBody) {
    this.createDistributorService
      .createDistributor(postBody)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // if (!postBody.assignAgent) {
            this.isSending.set(false);
            this.distributorCreated(this.i18n.message.createSuccess);
            return;
          // }

          // let user = this.msalService.instance.getActiveAccount();
          // let loginUserId = user.idTokenClaims["sub"];

          // let newDistributorId = (res as any).id;

          // let postBodyAssignAgent: Record<string, any> = {};
          // postBodyAssignAgent.distributorId = newDistributorId;
          // postBodyAssignAgent.userId = loginUserId;

          // this.assignDistributorAgentService
          //   .assignDistributorAgent(newDistributorId, loginUserId, postBody)
          //   .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
          //   .subscribe({
          //     next: (res) => {
          //       this.distributorCreated(this.i18n.message.createWithAdminSuccess);
          //     },
          //     error: (e) => {
          //       this.dialogRef.close({ needRefresh: true });
          //       this.snackBar.open(
          //         `${this.i18n.message.createAdminError}${JSON.stringify(e.error)}`,
          //         this.i18n.button.close,
          //         { duration: 10000 },
          //       );
          //     },
          //   });
        },
        error: (e) => {
          this.isSending.set(false);
          this.snackBar.open(
            `${this.i18n.message.createError}${JSON.stringify(e.error)}`,
            this.i18n.button.close,
            { duration: 10000 },
          );
        },
      });
  }

  distributorCreated(message) {
    this.dialogRef.close({ needRefresh: true });
    this.snackBar.open(message, this.i18n.button.close, { duration: 5000 });
  }

  updateDistributor(postParams) {
    this.updateDistributorService
      .updateDistributor(this.distributorId, postParams)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe({
        next: (res) => {
          this.dialogRef.close({ needRefresh: true });
          this.snackBar.open(
            this.i18n.message.updateSuccess,
            this.i18n.button.close,
            { duration: 5000 },
          );
        },
        error: (e) => {
          this.snackBar.open(
            this.i18n.message.updateError,
            this.i18n.button.close,
            { duration: 10000 },
          );
        },
      });
  }

  openDistributorAgent() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        "/section-group-admin/" + this.distributorId + "/home",
      ]),
    );
    window.open("#" + url, "_blank");
  }

  inviteAgent() {
    const dialogRef = this.dialog.open(DistributorAgentInviteComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        distributorId: this.distributorId,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {});
  }

  setChangeEvents(){
    this.distributorFormGroup.valueChanges
      .pipe(
        // 💡 コンポーネントが破棄されたときに自動で購読を解除し、メモリリークを防ぐ
        takeUntilDestroyed(this.destroyRef) 
      )
      .subscribe((rawValues: any) => {
        // 2. 変更があったら実行したい関数を呼び出す
        this.onFormValueChanged(rawValues);
      }
    );
  }

  onFormValueChanged(rawValues: any){
    // DebugUtils.dumpFormStructure(this.distributorFormGroup);
  }


  getAddressesGroup(): FormGroup {
    return this.distributorFormGroup.get('addresses') as FormGroup;
  }

  getContactDetailsGroup(): FormGroup {
    return this.distributorFormGroup.get('contactDetails') as FormGroup;
  }

  getBillingInfoGroup(): FormGroup {
    return this.distributorFormGroup.get('billingInfo') as FormGroup;
  }

  // getBillingTermsGroup(): FormGroup {
  //   return this.distributorFormGroup.get('billingTerms') as FormGroup;
  // }

  // getBillingSourceGroup(): FormGroup {
  //   return this.distributorFormGroup.get('billingSource') as FormGroup;
  // }

}

export enum DistributorDetailMode {
  disp,
  edit,
  create,
}

export interface DistributorDocument {
  id: string | null;

  name: string;

  addresses: Addresses;
  contactDetails: ContactDetails;
  billingInfo: BillingInfo;
  // billingSource: BillingSource;

  maxSectionGroups: number;

  functionAttributes: any;
  userDataTemplateChoices: any;

  commonInfo: {
    lastModifiedAt: string;
    lastModifiedBy: string;
  };

  agents: any[];
}
